import { Mail, Instagram } from 'lucide-react';
import { useState } from 'react';
import styled from 'styled-components';
import mixpanel from 'mixpanel-browser';
import { purpleColors, violetColors, slateColors, Section, fontFamily } from '../ContentPages/AppLandingPage/styles';
import { postApi } from '../../utils/api';
import { useTranslation } from 'react-i18next';

const StayUpdatedSection = styled(Section)`
  background: white;
  width: 100%;
  padding: 2rem 1rem;
  box-sizing: border-box;
  
  @media (min-width: 640px) {
    padding: 2.5rem 1.5rem;
  }
  
  @media (min-width: 768px) {
    padding: 3rem 3rem;
  }
`;

const Card = styled.div`
  background: linear-gradient(to bottom right, ${purpleColors[50]}, ${violetColors[500]}15, ${purpleColors[50]});
  border-radius: 1.5rem;
  padding: 2rem 1rem;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 640px) {
    padding: 3rem 1.5rem;
  }
  
  @media (min-width: 768px) {
    padding: 4rem;
  }
`;

const CardContent = styled.div`
  max-width: 48rem;
  margin: 0 auto;
  width: 100%;
  padding: 0 0.5rem;
  box-sizing: border-box;
  
  @media (min-width: 640px) {
    padding: 0;
  }
`;

const Heading = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${slateColors[900]};
  margin-bottom: 1rem;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (min-width: 640px) {
    font-size: 2.25rem;
  }
  
  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  color: ${slateColors[600]};
  margin-bottom: 2rem;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  width: 100%;
  
  @media (min-width: 640px) {
    flex-direction: row;
    align-items: center;
  }
`;

const EmailInput = styled.input`
  flex: 1;
  max-width: 100%;
  padding: 0.875rem 1.25rem;
  border-radius: 9999px;
  border: 2px solid ${slateColors[200]};
  font-family: ${fontFamily};
  font-size: 1rem;
  color: ${slateColors[900]};
  width: 100%;
  box-sizing: border-box;
  
  @media (min-width: 640px) {
    max-width: 28rem;
    padding: 1rem 1.5rem;
  }
  
  &:focus {
    outline: none;
    border-color: ${purpleColors[500]};
  }
  
  &::placeholder {
    color: ${slateColors[400]};
  }
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  padding: 0.875rem 1.5rem;
  background: linear-gradient(to right, ${purpleColors[600]}, ${violetColors[600]});
  color: white;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: ${props => props.disabled ? 0.5 : 1};
  width: 100%;
  white-space: nowrap;
  box-sizing: border-box;
  
  @media (min-width: 640px) {
    width: auto;
    min-width: 140px;
    padding: 1rem 2rem;
  }
  
  &:hover:not(:disabled) {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  border-top: 1px solid ${slateColors[200]};
  padding-top: 2rem;
`;

const SocialHeading = styled.p`
  color: ${slateColors[600]};
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  
  @media (min-width: 640px) {
    font-size: 1rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  width: 100%;
  
  @media (min-width: 640px) {
    gap: 1.5rem;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: white;
  border-radius: 9999px;
  border: 2px solid ${slateColors[200]};
  transition: all 0.2s;
  text-decoration: none;
  flex-shrink: 0;
  
  @media (min-width: 640px) {
    gap: 0.75rem;
    padding: 0.75rem 1.5rem;
  }
  
  &:hover {
    border-color: ${purpleColors[500]};
    
    svg, span {
      color: ${purpleColors[600]};
    }
  }
  
  svg {
    width: 1.125rem;
    height: 1.125rem;
    color: ${slateColors[700]};
    transition: color 0.2s;
    flex-shrink: 0;
    
    @media (min-width: 640px) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
  
  span {
    font-weight: 500;
    color: ${slateColors[700]};
    transition: color 0.2s;
    font-size: 0.875rem;
    white-space: nowrap;
    
    @media (min-width: 640px) {
      font-size: 1rem;
    }
  }
`;

export default function StayUpdated() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await postApi('newsletter-subscribe', { email });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to subscribe' }));
        throw new Error(errorData.error || 'Failed to subscribe');
      }

      await response.json();
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
    } catch (error: any) {
      console.error('Error subscribing to newsletter:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <StayUpdatedSection id="newsletter">
        <Card>
          <CardContent>
            <Heading>{t('footerSection.stayUpdated.heading')}</Heading>
            <Description>{t('footerSection.stayUpdated.description')}</Description>

            <Form onSubmit={handleSubmit}>
              <EmailInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footerSection.stayUpdated.emailPlaceholder')}
                required
              />
              <SubmitButton
                type="submit"
                disabled={status === 'submitting'}
                onClick={() => {
                  mixpanel.track('Newsletter subscribe button clicked', {
                    page: window.location.pathname,
                  });
                }}
              >
                <Mail />
                {status === 'submitting' ? t('footerSection.stayUpdated.subscribing') : status === 'success' ? t('footerSection.stayUpdated.subscribed') : t('footerSection.stayUpdated.subscribe')}
              </SubmitButton>
            </Form>

            <Divider>
              <SocialHeading>{t('footerSection.stayUpdated.socialHeading')}</SocialHeading>
              <SocialLinks>
                <SocialLink
                  href="https://instagram.com/chuuchuu.app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Instagram />
                  <span>Instagram</span>
                </SocialLink>
                <SocialLink
                  href="https://www.threads.com/@chuuchuu.app"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 192 192" fill="currentColor">
                    <path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z"/>
                  </svg>
                  <span>Threads</span>
                </SocialLink>
                <SocialLink
                  href="https://www.linkedin.com/company/chuuchuu"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 72 72" fill="currentColor">
                    <path d="M62,62 L51.315625,62 L51.315625,43.8021149 C51.315625,38.8127542 49.4197917,36.0245323 45.4707031,36.0245323 C41.1746094,36.0245323 38.9300781,38.9261103 38.9300781,43.8021149 L38.9300781,62 L28.6333333,62 L28.6333333,27.3333333 L38.9300781,27.3333333 L38.9300781,32.0029283 C38.9300781,32.0029283 42.0260417,26.2742151 49.3825521,26.2742151 C56.7356771,26.2742151 62,30.7644705 62,40.051212 L62,62 Z M16.349349,22.7940133 C12.8420573,22.7940133 10,19.9296567 10,16.3970067 C10,12.8643566 12.8420573,10 16.349349,10 C19.8566406,10 22.6970052,12.8643566 22.6970052,16.3970067 C22.6970052,19.9296567 19.8566406,22.7940133 16.349349,22.7940133 Z M11.0325521,62 L21.769401,62 L21.769401,27.3333333 L11.0325521,27.3333333 L11.0325521,62 Z"/>
                  </svg>
                  <span>LinkedIn</span>
                </SocialLink>
                <SocialLink
                  href="https://bsky.app/profile/chuuchuuapp.bsky.social"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
                  </svg>
                  <span>Bluesky</span>
                </SocialLink>
                <SocialLink
                  href="https://mastodon.social/@chuuchuu"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.268 5.313c-.35-2.578-2.617-4.61-5.304-5.004C17.51.242 15.792 0 11.813 0h-.03c-3.98 0-4.835.242-5.288.309C3.882.692 1.496 2.518.917 5.127.64 6.412.61 7.837.661 9.143c.074 1.874.088 3.745.26 5.611.118 1.24.325 2.47.62 3.68.55 2.237 2.777 4.098 4.96 4.857 2.336.792 4.849.923 7.256.38.265-.061.527-.132.786-.213.585-.184 1.27-.39 1.774-.753a.057.057 0 0 0 .023-.043v-1.809a.052.052 0 0 0-.02-.041.053.053 0 0 0-.046-.01 20.282 20.282 0 0 1-4.709.545c-2.73 0-3.463-1.284-3.674-1.818a5.593 5.593 0 0 1-.319-1.433.053.053 0 0 1 .066-.054c1.517.363 3.072.546 4.632.546.376 0 .75 0 1.125-.01 1.57-.044 3.224-.124 4.768-.422.038-.008.077-.015.11-.024 2.435-.464 4.753-1.92 4.989-5.604.008-.145.03-1.52.03-1.67.002-.512.167-3.63-.024-5.545zm-3.748 9.195h-2.561V8.29c0-1.309-.55-1.976-1.67-1.976-1.23 0-1.846.79-1.846 2.35v3.403h-2.546V8.663c0-1.56-.617-2.35-1.848-2.35-1.112 0-1.668.668-1.67 1.977v6.218H4.822V8.102c0-1.31.337-2.35 1.011-3.12.696-.77 1.608-1.164 2.74-1.164 1.311 0 2.302.5 2.962 1.498l.638 1.06.638-1.06c.66-.999 1.65-1.498 2.96-1.498 1.13 0 2.043.395 2.74 1.164.675.77 1.012 1.81 1.012 3.12z"/>
                  </svg>
                  <span>Mastodon</span>
                </SocialLink>
              </SocialLinks>
            </Divider>
          </CardContent>
        </Card>
    </StayUpdatedSection>
  );
}
